import React from 'react';
import { ReportData } from '../../types/report.types';

interface ToolsApproachEditorV2Props {
  reportData: ReportData;
  onDataChange: (field: string, value: any) => void;
}

const ToolsApproachEditorV2: React.FC<ToolsApproachEditorV2Props> = ({ reportData, onDataChange }) => {
  const isNetworkTarget = (t: any) => {
    const type = (t.target_type || t.type || '').toLowerCase();
    const value = (t.target_value || t.url || t.target_name || '').toLowerCase();
    if (type.includes('web') || type.includes('api') || type.includes('app') || type.includes('mobile') || type.includes('ios') || type.includes('android')) {
      return false;
    }
    if (value.startsWith('http://') || value.startsWith('https://')) {
      return false;
    }
    return type.includes('network') || type.includes('infra') || type.includes('subnet') || type.includes('range') || type.includes('ip') ||
           !!value.match(/^\d{1,3}\.\d{1,3}\.\d{1,3}\.\d{1,3}/) ||
           /\/\s*\d{1,2}\s*$/.test(value);
  };

  const targets = reportData.target_details || [];
  const networkTargets = targets.filter(isNetworkTarget);
  const webTargets = targets.filter((t: any) => !isNetworkTarget(t));
  const isNetwork = reportData.testing_type?.toLowerCase().includes('network') ||
    reportData.program_details?.[0]?.testing_type?.toLowerCase().includes('network') || (networkTargets.length > 0 && webTargets.length === 0);

  const defaultWebTools = [
    { name: 'Burp Suite Professional', purpose: 'Intercepting proxy, manual testing & scanning' },
    { name: 'OWASP ZAP', purpose: 'Automated scanning & spidering' },
    { name: 'sqlmap', purpose: 'SQL injection detection & exploitation' },
    { name: 'ffuf / dirsearch', purpose: 'Content & directory discovery' },
    { name: 'nuclei / nikto', purpose: 'Known-vulnerability & misconfiguration checks' },
    { name: 'jwt_tool', purpose: 'JWT analysis & tampering' },
    { name: 'Postman', purpose: 'API request crafting & testing' },
    { name: 'Nessus', purpose: 'Authenticated vulnerability scanning' }
  ];

  const defaultNetworkTools = [
    { name: 'Nmap', purpose: 'Host discovery, port & service enumeration' },
    { name: 'Nessus', purpose: 'Authenticated vulnerability scanning' },
    { name: 'CrackMapExec', purpose: 'SMB enumeration, pass-the-hash, lateral movement' },
    { name: 'Impacket (ntlmrelayx, GetUserSPNs, secretsdump)', purpose: 'NTLM relay, Kerberoasting, credential dumping' },
    { name: 'Responder', purpose: 'LLMNR/NBT-NS poisoning' },
    { name: 'BloodHound', purpose: 'Active Directory attack-path analysis' },
    { name: 'Metasploit', purpose: 'Exploitation & validation' },
    { name: 'hashcat', purpose: 'Offline password / ticket cracking' }
  ];

  const defaultWebApproach = 'The assessment combined automated scanning with in-depth manual testing. Automated tools provided broad coverage and initial discovery; every candidate issue was then manually verified and safely exploited to confirm real-world impact and eliminate false positives. Testing was performed from both unauthenticated and authenticated perspectives across the provided user roles, following the OWASP WSTG and mapped to the OWASP Top 10 (2021).';

  const defaultNetworkApproach = 'Testing was performed from an assumed-breach position on the corporate LAN, combining automated discovery and scanning with in-depth manual exploitation. Every issue was manually verified to confirm impact and remove false positives; destructive techniques were avoided.';

  const defaultTools = isNetwork ? defaultNetworkTools : defaultWebTools;
  const defaultApproach = isNetwork ? defaultNetworkApproach : defaultWebApproach;

  let parsedConfig: any = { globalFields: { description: true, instructions: true, impact: true, fix: true, evidence: true }, individualFindings: {} };
  if (reportData.finding_config) {
    if (typeof reportData.finding_config === 'string') {
      try {
        parsedConfig = JSON.parse(reportData.finding_config);
      } catch (_) {}
    } else {
      parsedConfig = reportData.finding_config;
    }
  }
  const config = parsedConfig;
  const toolsConfig = config.toolsApproach || {};

  const approachText = toolsConfig.approach !== undefined ? toolsConfig.approach : defaultApproach;
  const tools: { name: string; purpose: string }[] = Array.isArray(toolsConfig.tools) ? toolsConfig.tools : defaultTools;

  const handleApproachChange = (value: string) => {
    const updated = {
      ...toolsConfig,
      approach: value
    };
    onDataChange('finding_config', { ...config, toolsApproach: updated });
  };

  const handleToolChange = (index: number, field: 'name' | 'purpose', value: string) => {
    const updatedTools = tools.map((t: { name: string; purpose: string }, idx: number) =>
      idx === index ? { ...t, [field]: value } : t
    );
    onDataChange('finding_config', {
      ...config,
      toolsApproach: {
        ...toolsConfig,
        tools: updatedTools
      }
    });
  };

  const handleAddTool = () => {
    onDataChange('finding_config', {
      ...config,
      toolsApproach: {
        ...toolsConfig,
        tools: [...tools, { name: '', purpose: '' }]
      }
    });
  };

  const handleRemoveTool = (index: number) => {
    const updatedTools = tools.filter((_: { name: string; purpose: string }, idx: number) => idx !== index);
    onDataChange('finding_config', {
      ...config,
      toolsApproach: {
        ...toolsConfig,
        tools: updatedTools
      }
    });
  };

  return (
    <div className="h-full overflow-y-auto space-y-6">
      {/* Approach Paragraph */}
      <div className="bg-white rounded-xl shadow-lg border border-slate-100 p-6">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-1 h-8 bg-gradient-to-b from-blue-500 to-blue-700 rounded-full" />
          <div>
            <h3 className="text-base font-bold text-blue-900 tracking-tight">Assessment Approach Description</h3>
            <p className="text-xs text-blue-700/80 font-medium mt-0.5">Write the testing approach description</p>
          </div>
        </div>
        <div className="pt-2">
          <textarea
            value={approachText}
            onChange={(e) => handleApproachChange(e.target.value)}
            className="w-full px-3 py-2 text-xs border border-slate-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-400"
            rows={4}
          />
        </div>
      </div>

      {/* Tools Table - styled to match PDF output */}
      <div className="bg-white rounded-xl shadow-lg border border-slate-100 p-6">
        <div className="flex items-center gap-3 mb-4 justify-between">
          <div className="flex items-center gap-3">
            <div className="w-1 h-8 bg-gradient-to-b from-blue-500 to-blue-700 rounded-full" />
            <div>
              <h3 className="text-base font-bold text-blue-900 tracking-tight">Tools Catalog</h3>
              <p className="text-xs text-blue-700/80 font-medium mt-0.5">Click any cell to edit inline</p>
            </div>
          </div>
          <button
            onClick={handleAddTool}
            className="px-3 py-1.5 bg-blue-600 text-white text-xs rounded hover:bg-blue-700 transition shadow-sm"
          >
            + Add Tool
          </button>
        </div>

        {/* Table matching PDF style */}
        <div className="rounded-lg overflow-hidden border border-slate-200">
          {/* Header */}
          <div className="grid grid-cols-[35%_1fr_auto] bg-blue-900 text-white">
            <div className="px-4 py-2.5 text-xs font-bold uppercase tracking-wide">Tool</div>
            <div className="px-4 py-2.5 text-xs font-bold uppercase tracking-wide border-l border-blue-700">Purpose</div>
            <div className="px-3 py-2.5 text-xs font-bold uppercase tracking-wide border-l border-blue-700 w-16 text-center">Del</div>
          </div>

          {/* Rows */}
          {tools.map((t: { name: string; purpose: string }, index: number) => (
            <div
              key={index}
              className={`grid grid-cols-[35%_1fr_auto] border-t border-slate-200 ${index % 2 === 0 ? 'bg-slate-50' : 'bg-white'}`}
            >
              <input
                type="text"
                value={t.name}
                onChange={(e) => handleToolChange(index, 'name', e.target.value)}
                className={`px-4 py-2 text-xs font-semibold text-slate-800 bg-transparent border-none outline-none focus:bg-blue-50 focus:ring-1 focus:ring-inset focus:ring-blue-300 w-full ${index % 2 === 0 ? 'bg-slate-50' : 'bg-white'}`}
                placeholder="Tool name..."
              />
              <input
                type="text"
                value={t.purpose}
                onChange={(e) => handleToolChange(index, 'purpose', e.target.value)}
                className={`px-4 py-2 text-xs text-slate-600 bg-transparent border-none border-l border-slate-200 outline-none focus:bg-blue-50 focus:ring-1 focus:ring-inset focus:ring-blue-300 w-full ${index % 2 === 0 ? 'bg-slate-50' : 'bg-white'}`}
                placeholder="Purpose / description..."
              />
              <button
                onClick={() => handleRemoveTool(index)}
                className="w-16 flex items-center justify-center border-l border-slate-200 text-red-400 hover:text-red-600 hover:bg-red-50 transition-colors"
                title="Remove tool"
              >
                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          ))}

          {tools.length === 0 && (
            <div className="px-4 py-6 text-center text-xs text-slate-400">
              No tools added yet. Click "+ Add Tool" to add one.
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ToolsApproachEditorV2;
